import React, {FC, useCallback, useEffect, useRef} from 'react';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {Counter, CounterSize} from '@wix/wix-ui-tpa/cssVars';
import cx from 'classnames';
import {st, classes as stylableClasses} from './ProductsQuantity.st.css';
import {st as stOld, classes as oldStylableClasses} from './ProductsQuantityOld.st.css';
import scssClasses from './ProductQuantity.scss';
import {UserInputType} from '../../../constants';
import {useIsRenderedFrom} from '../../../providers/renderContext';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import {SPECS} from '../../../specs';

export enum ProductQuantityDataHooks {
  container = 'product-quantity-container',
  counter = 'product-quantity-counter',
}

export interface ProductQuantityProps {
  value?: number;
  onChange?(value: number): void;
  className?: string;
  showErrors?: boolean;
}

function getQuantityErrorMessage(
  value: number,
  quantityRange: {min: number; max: number},
  t: IProvidedTranslationProps['t']
): string | null {
  if (value > quantityRange.max) {
    return t('QUANTITY_EXCEEDS_INVENTORY', {inventory: quantityRange.max});
  }
  if (value < quantityRange.min) {
    return t('QUANTITY_BELOW_MINIMUM_RANGE', {minimum: quantityRange.min});
  }
  return null;
}

const ProductQuantityComponent: FC<ProductQuantityProps & ProvidedGlobalProps & IProvidedTranslationProps> = ({
  t,
  value: valueProp,
  globals: {
    quantityRange,
    isProductSubmitted,
    userInputErrors,
    scrollAndFocusInputTarget,
    isQuickView,
    experiments,
    handleUserInput,
    validateUserInputsChanges,
    userInputs,
    useExperiments,
  },
  onChange,
  className,
  showErrors = true,
}) => {
  const isRenderedFromStickyDrawer = useIsRenderedFrom('stickyDrawer');
  const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StickyAddToCartMobile);
  const isStickyDrawer = isStickyAddToCartEnabled && isRenderedFromStickyDrawer;

  const value = isStickyAddToCartEnabled ? userInputs[UserInputType.Quantity][0] : valueProp;

  const quantityContainerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    const isScrollAndFocusInputTarget = UserInputType.Quantity === scrollAndFocusInputTarget?.inputType;
    const shouldScroll = isScrollAndFocusInputTarget && quantityContainerRef.current;

    if (shouldScroll) {
      scrollAndFocusUserInput(UserInputType.Quantity, quantityContainerRef.current, isQuickView);
    }
  }, [scrollAndFocusInputTarget, quantityContainerRef, isQuickView, experiments]);

  const onQuantityChange = useCallback(
    (validValue: number) => {
      handleUserInput(UserInputType.Quantity, validValue, 0);
      validateUserInputsChanges();
    },
    [handleUserInput, validateUserInputsChanges]
  );

  const handleInputChange = useCallback(
    (inputValue: string) => {
      const parsedValue = Number(inputValue);
      const validValue = isNaN(parsedValue) || inputValue === '' ? value : parsedValue;
      if (!isStickyAddToCartEnabled) {
        onChange(validValue);
        return;
      }

      onQuantityChange(validValue);
    },
    [onChange, value, isStickyAddToCartEnabled, onQuantityChange]
  );

  /* istanbul ignore next: tested in sled */
  const isError = isProductSubmitted && userInputErrors[UserInputType.Quantity][0];
  /* istanbul ignore next: tested in sled */
  const errorMessage = getQuantityErrorMessage(value, quantityRange, t);

  const title = t('QUANTITY_LABEL');

  // istanbul ignore next
  const isNewStyles = experiments.productPageUpliftProductOptionsViewer && !isQuickView;
  const counterRootClassName = isNewStyles
    ? st(stylableClasses.counterRoot, {stickyDrawer: isStickyDrawer})
    : stOld(oldStylableClasses.counterRoot);

  const shouldShowErrors = showErrors || !isStickyAddToCartEnabled;
  const errorProps = shouldShowErrors ? {error: isError, errorMessage, newErrorMessage: true} : {};

  return (
    <div ref={quantityContainerRef} data-hook={ProductQuantityDataHooks.container}>
      <Counter
        size={CounterSize.medium}
        className={cx(counterRootClassName, scssClasses.counterRoot, className)}
        data-hook={ProductQuantityDataHooks.counter}
        onChange={handleInputChange}
        value={value}
        max={quantityRange.max}
        min={quantityRange.min}
        step={1}
        label={title}
        inputAriaLabel={title}
        {...errorProps}
        required
        showAsterisk
      />
    </div>
  );
};

export const ProductQuantity = withGlobalProps(withTranslations('globals.texts')(ProductQuantityComponent));
